import { toast } from "@/components/common/Toast"
import {
  createEmptyChat,
  determineSourceOrigin,
  isPersistedConversationId,
} from "@/components/feature/Chat/utils/chatUtils"
import { CONVERSATIONS_LOAD_ERROR_TOAST_ID } from "@/constants/UiConstants"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { hydrateTurnBlocks } from "@/features/agent-chat/engine/turnBlocks"
import { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  normalizeSourceMetadata,
  RawSourceMetadata,
} from "@/services/api/assistantChatService"
import { useGetChatConversationMessages } from "@/services/api/chatConversationService"
import { transformCitationsFromApi } from "@/services/api/transformCitationsFromApi"
import { Chat, Message as ChatMessage } from "@/types/assistantChat"
import { ChatConversationMessage } from "@/types/chatConversation"
import React from "react"
import { useSearchParams } from "react-router"

interface UseConversationRoutingProps {
  chat: Chat
  setChat: React.Dispatch<React.SetStateAction<Chat>>
  translate: (key: TranslationKeys) => string
  /**
   * When false, the active conversation is derived from `chat.id` instead of
   * the `conversation` URL query param, and the hook never reads/writes the
   * URL. Used when ChatInterface is rendered outside of the /chat route
   * (e.g. inside the nav-header chat sheet) so it doesn't rewrite the host
   * page's URL.
   */
  syncUrl?: boolean
  /**
   * Called when the URL's `conversation` param names a genuinely different
   * conversation than the one currently loaded (browser back/forward, a
   * pasted link) — as opposed to this same conversation's first hydration,
   * which does not call this. Wire to `AgentChatContext.resetForNewConversation`.
   */
  onConversationSwitch?: () => void
}

function mapConversationMessagesToChat(
  conversationMessages: ChatConversationMessage[],
): ChatMessage[] {
  return [...conversationMessages]
    .sort((left, right) => left.sequenceNumber - right.sequenceNumber)
    .map((message) => {
      const rawCitations = message.metadata?.citationsJson?.citations
      const citations = Array.isArray(rawCitations)
        ? transformCitationsFromApi(rawCitations)
        : undefined
      const sourceMetadata = normalizeSourceMetadata(
        message.metadata?.sourceMetadataJson as RawSourceMetadata,
      )
      const sourceOrigin =
        message.metadata?.sourceOrigin ??
        determineSourceOrigin(citations ?? [], sourceMetadata)

      return {
        role: message.role,
        content: message.content,
        citations,
        sourceMetadata,
        sourceOrigin,
        // Absent for a turn that ran no tools and for every turn persisted
        // before activity tracking existed — those render from `content`.
        blocks: hydrateTurnBlocks(message.metadata?.activityJson),
      }
    })
}

export function useConversationRouting({
  chat,
  setChat,
  translate,
  syncUrl = true,
  onConversationSwitch,
}: UseConversationRoutingProps) {
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const [searchParams, setSearchParams] = useSearchParams()
  const lastHydratedVersionRef = React.useRef<string | null>(null)
  // A ref, not an effect dependency: the hydration guard below needs the LIVE
  // count, and listing `chat.messages` would re-run that effect on every
  // streamed chunk just to fall through the same early return.
  const heldMessageCountRef = React.useRef(0)
  heldMessageCountRef.current = chat.messages?.length ?? 0

  const activeConversationId = syncUrl
    ? searchParams.get("conversation")
    : isPersistedConversationId(chat.id)
      ? chat.id
      : null
  const hasPersistedConversationInUrl =
    syncUrl && isPersistedConversationId(activeConversationId)

  const shouldHydrateConversation =
    chatHistoryEnabled && isPersistedConversationId(activeConversationId)

  const {
    data: conversationMessages,
    error: hydrationError,
    isPending,
    isFetching,
    isError: hasHydrationError,
  } = useGetChatConversationMessages(
    activeConversationId || undefined,
    shouldHydrateConversation,
  )
  const isHydratingConversation =
    shouldHydrateConversation && (isPending || isFetching)

  // A conversation ID that's been client-minted (e.g. by an attach-only
  // session) but never persisted has no row yet — the messages GET 404s.
  // That's a fresh, empty conversation, not a failure: suppress the toast.
  const isUnpersistedConversation =
    (hydrationError as { response?: { status?: number } } | null)?.response
      ?.status === 404

  React.useEffect(() => {
    if (
      !chatHistoryEnabled ||
      !hasHydrationError ||
      !activeConversationId ||
      isHydratingConversation ||
      isUnpersistedConversation
    ) {
      return
    }
    toast.error(translate("AI_CHAT/HISTORY/LIST_ERROR"), {
      id: CONVERSATIONS_LOAD_ERROR_TOAST_ID,
    })
  }, [
    activeConversationId,
    chatHistoryEnabled,
    hasHydrationError,
    isHydratingConversation,
    isUnpersistedConversation,
    translate,
  ])

  React.useEffect(() => {
    if (!shouldHydrateConversation || !activeConversationId) {
      lastHydratedVersionRef.current = null
      return
    }

    if (chat.id !== activeConversationId) {
      setChat((previousChat) => ({
        ...createEmptyChat(),
        id: activeConversationId,
        webSearchEnabled: previousChat.webSearchEnabled ?? false,
      }))
      onConversationSwitch?.()
    }

    if (isHydratingConversation || !conversationMessages) {
      return
    }

    // Already holding this conversation: hydration loads a thread the user
    // OPENED, it does not re-read one this session wrote.
    //
    // Without this, the first turn of every conversation gets overwritten
    // while still on screen: `chat.id` starts as an unpersisted placeholder
    // and only takes the real id when the stream echoes it back mid-turn,
    // which flips this query on and fetches the turn we just authored. The
    // fetched copy is lossier than what is in memory — no `blocks` (so the
    // narration collapses back into the reply) and no `turnId` (so a
    // continuation would append a duplicate message instead of updating its
    // own). A genuine switch is unaffected: that path resets to an empty chat
    // first, so this guard is false on the pass that matters.
    if (chat.id === activeConversationId && heldMessageCountRef.current > 0) {
      return
    }

    const hydratedVersion = `${activeConversationId}:${conversationMessages.length}:${conversationMessages
      .map((message) => `${message.id}:${message.sequenceNumber}`)
      .join("|")}`

    if (lastHydratedVersionRef.current === hydratedVersion) {
      return
    }

    const mappedMessages = mapConversationMessagesToChat(conversationMessages)
    const createdAt =
      conversationMessages[0]?.createdAt?.toISOString?.() ||
      new Date().toISOString()
    const updatedAt =
      conversationMessages[
        Math.max(0, conversationMessages.length - 1)
      ]?.createdAt?.toISOString?.() || new Date().toISOString()

    setChat((previousChat) => ({
      ...createEmptyChat(),
      id: activeConversationId,
      messages: mappedMessages,
      created_at: createdAt,
      updated_at: updatedAt,
      webSearchEnabled: previousChat.webSearchEnabled ?? false,
    }))

    lastHydratedVersionRef.current = hydratedVersion
  }, [
    activeConversationId,
    chat.id,
    conversationMessages,
    isHydratingConversation,
    onConversationSwitch,
    setChat,
    shouldHydrateConversation,
  ])

  React.useEffect(() => {
    if (
      !syncUrl ||
      !chatHistoryEnabled ||
      !isPersistedConversationId(chat.id) ||
      hasPersistedConversationInUrl
    ) {
      return
    }

    if (chat.id === activeConversationId) {
      return
    }

    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set("conversation", chat.id)
    setSearchParams(nextSearchParams, { replace: true })
  }, [
    activeConversationId,
    chat.id,
    hasPersistedConversationInUrl,
    searchParams,
    setSearchParams,
    syncUrl,
  ])

  const clearActiveConversationFromUrl = React.useCallback(() => {
    if (!syncUrl) {
      return
    }
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete("conversation")
    setSearchParams(nextSearchParams, { replace: true })
  }, [searchParams, setSearchParams, syncUrl])

  return {
    activeConversationId,
    isHydratingConversation,
    clearActiveConversationFromUrl,
  }
}
